import { useNavigate } from 'react-router-dom'
import { useWizardStore, TOTAL_STEPS } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface ConfiguratorLayoutProps {
  lang: Lang
  children: React.ReactNode
  stepTitle: string
  phaseLabel: string
}

const COPY = {
  fr: {
    tagline: 'Configuration Engine',
    cancel: 'Annuler',
    next: 'Suivant',
    finish: 'Finaliser',
    status: 'Système prêt pour la configuration robot.fric.link',
  },
  en: {
    tagline: 'Configuration Engine',
    cancel: 'Cancel',
    next: 'Next',
    finish: 'Finish',
    status: 'System ready for robot.fric.link configuration',
  },
}

export default function ConfiguratorLayout({
  lang,
  children,
  stepTitle,
  phaseLabel,
}: ConfiguratorLayoutProps) {
  const navigate = useNavigate()
  const { currentStep, goNext, goPrev, canProceedCurrent } = useWizardStore()
  const t = COPY[lang]

  const progressPct = Math.round((currentStep / TOTAL_STEPS) * 100)
  const stepLabel = String(currentStep).padStart(2, '0')
  const totalLabel = String(TOTAL_STEPS).padStart(2, '0')
  const isLast = currentStep === TOTAL_STEPS
  const canGo = canProceedCurrent()

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto">
      {/* Progress stepper */}
      <div className="mb-12">
        <div className="flex justify-between items-end mb-4 gap-6">
          <div className="flex-1">
            <span className="text-secondary font-headline text-sm tracking-[0.2em] font-medium uppercase">
              {t.tagline}
            </span>
            <h1 className="text-3xl md:text-4xl font-headline font-bold text-on-surface tracking-tight mt-2">
              {stepTitle}
            </h1>
          </div>
          <div className="text-right shrink-0">
            <span className="block text-primary font-headline text-2xl font-bold leading-none">
              {stepLabel}
              <span className="text-on-surface-variant/40 text-lg">/{totalLabel}</span>
            </span>
            <span className="text-xs text-on-surface-variant font-label tracking-widest uppercase mt-1 block">
              {phaseLabel}
            </span>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1 w-full bg-surface-container-highest overflow-hidden rounded-full">
          <div
            className="h-full bg-gradient-to-r from-primary-container to-secondary-container transition-all duration-700"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Step content */}
      <div className="min-h-[420px]">{children}</div>

      {/* Action footer */}
      <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-8 pt-8 border-t border-outline-variant/10">
        <div className="flex items-center gap-4 text-on-surface-variant">
          <div className="w-3 h-3 bg-secondary rounded-full blur-[2px] animate-pulse" />
          <span className="text-xs font-label tracking-widest uppercase">{t.status}</span>
        </div>
        <div className="flex items-center gap-6">
          {currentStep > 1 ? (
            <button
              onClick={goPrev}
              className="text-on-surface-variant hover:text-on-surface font-headline text-sm tracking-widest uppercase transition-colors px-6 py-3"
            >
              ← Retour
            </button>
          ) : (
            <button
              onClick={() => void navigate('/')}
              className="text-on-surface-variant hover:text-on-surface font-headline text-sm tracking-widest uppercase transition-colors px-6 py-3"
            >
              {t.cancel}
            </button>
          )}
          <button
            onClick={isLast ? () => void navigate('/dashboard') : goNext}
            disabled={!canGo}
            className="bg-primary-fixed-dim text-on-primary-fixed font-headline font-bold tracking-widest uppercase text-sm px-10 py-4 rounded-lg flex items-center gap-3 hover:shadow-[0_0_20px_rgba(0,219,233,0.3)] transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            {isLast ? t.finish : t.next}
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      </div>
    </div>
  )
}
